import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from '@chakra-ui/react';
import useCustomReportsApi from '../api/useCustomReportsApi';

export function useCustomReportsList() {
  const api = useCustomReportsApi();

  return useQuery(
    'custom-reports',
    async () => api.getCustomReportsList(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    },
  );
}

export function useCustomReport(selectedCustomReportId) {
  const api = useCustomReportsApi();

  return useQuery(
    ['custom-report', selectedCustomReportId],
    async () => api.getCustomReport(selectedCustomReportId),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      enabled: !!selectedCustomReportId, // Only run if an ID is provided
    },
  );
}

export function useAccessLevels() {
  const api = useCustomReportsApi();

  return useQuery(
    'custom-reports-access-levels',
    async () => api.getAccessLevels(),
  );
}

export const useCreateCustomReport = () => {
  const api = useCustomReportsApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (customReport) => api.createCustomReport(customReport).then(({ data }) => data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-reports');
        toast({
          title: 'Success',
          description: 'Report was successfully created.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Report could not be created. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};

export const useUpdateCustomReport = () => {
  const api = useCustomReportsApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (customReport) => api.createCustomReport(customReport).then(({ data }) => data),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries('custom-reports');
        queryClient.invalidateQueries(['custom-report', variables.id]);
        toast({
          title: 'Success',
          description: 'Report was successfully updated.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Report could not be updated. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};

export const useDeleteCustomReport = () => {
  const api = useCustomReportsApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (id) => api.deleteCustomReport(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-reports');
        queryClient.invalidateQueries('custom-report');
        toast({
          title: 'Success',
          description: 'Report was successfully deleted.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Report could not be deleted. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};

export const useUpdateDisplayOrder = () => {
  const api = useCustomReportsApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation(
    (displayOrder) => api.updateDisplayOrder(displayOrder),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('custom-reports');
        toast({
          title: 'Success',
          description: 'Display order was successfully updated.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Error',
          description: error.response.data.Message ? error.response.data.Message : 'Display order could not be updated. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );
};
